<template>
    <div>
        <v-card class="order-form" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3">{{ $t('order_edit.heading') }}</div>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-row class="mt-3 mx-0">
                    <v-col cols="12">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-alt</v-icon>
                                {{ $t('order_edit.section_document_info') }}
                            </div>

                            <div class="section-content">
                                <v-row>

                                    <v-col cols="12" sm="3">
                                        <v-menu
                                            v-model="picker1"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.last_activity_at"
                                                    :error-messages="form.$errors.last_activity_at"
                                                    :label="$t('general.date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    @blur="form.validate('last_activity_at')"
                                                    @input="delete form.$errors.last_activity_at"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.last_activity_at"
                                                @input="picker1 = false; form.validate('last_activity_at')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="12" sm="3">
                                        <v-menu
                                            v-model="picker2"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.due_date"
                                                    :error-messages="form.$errors.due_date"
                                                    :label="$t('general.due_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    @blur="form.validate('due_date')"
                                                    @input="delete form.$errors.due_date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.due_date"
                                                @input="picker2 = false; form.validate('due_date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col class="d-flex" cols="12" sm="6">
                                        <v-autocomplete
                                            v-model="form.client_id"
                                            :items="clients"
                                            item-text="name"
                                            item-value="id"
                                            :filter="(item, queryText, itemText) => item.matches(queryText)"
                                            dense
                                            :label="$tc('general.client', 1)"
                                            menu-props="offsetY"
                                            :error-messages="form.$errors.client_id"
                                            @blur="form.validate('client_id')"
                                            @input="delete form.$errors.client_id"
                                        />

                                        <v-checkbox
                                            v-model="form.status"
                                            class="ml-8 mr-2"
                                            :label="$t('general.advance')"
                                            value="advance"
                                            dense
                                            @change="form.status = $event ? 'advance' : 'paid'"
                                        />
                                    </v-col>

                                    <v-col cols="12" sm="6" v-if="locale === 'me'">
                                        <v-text-field
                                            v-model="form.iic"
                                            label="JIKR"
                                            dense
                                            :error-messages="form.$errors.iic"
                                            @blur="form.validate('iic')"
                                            @input="delete form.$errors.iic"
                                        />
                                    </v-col>

                                    <v-col cols="12" sm="6" v-if="locale === 'me'">
                                        <v-text-field
                                            v-model="form.fiscal_code"
                                            label="IKOF"
                                            dense
                                            :error-messages="form.$errors.fiscal_code"
                                            @blur="form.validate('fiscal_code')"
                                            @input="delete form.$errors.fiscal_code"
                                        />
                                    </v-col>
                                </v-row>
                            </div>

                            <div class="d-flex justify-end mt-2">
                                <v-btn
                                    outlined
                                    color="primary"
                                    @click="save"
                                >
                                    <v-icon left>fal fa-check</v-icon>
                                    {{ $t('general.save') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="order?.items || []"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:header.actions="{ on, props }">
                        <div class="d-flex justify-end">
                            <v-btn
                                icon
                                large
                                color="primary"
                                :disabled="!order"
                                @click="openItemForm()"
                            >
                                <v-icon>fal fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:item="{ item }">
                        <tr
                            :class="{
                                'font-weight-medium': true,
                            }"
                        >
                            <td>{{ item.name }}</td>
                            <td>{{ item.getCount() }}</td>
                            <td>{{ item.getUnitPrice().toFormat('0.00') }}</td>
                            <td>{{ item.getDiscountPercent() }}%</td>

                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openItemForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteItem(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <order-item-edit
            :show.sync="showItemForm"
            :item="selectedItem"
            :order="order"
            @save="onItemSave($event)"
        />
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import OrderItemEdit from '@/views/shared/admin/orders/OrderItemEdit';
import Order from '@/models/Order';
import Client from '@/models/Client';
import { loadData } from '@/views/shared/loadPageData';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
    components: {
        OrderItemEdit,
    },

    async created() {
        if(this.$route.params.id) {
            const data = await loadData(`/admin/orders/${this.$route.params.id}`);
            data.order.items.reverse()
            this.order = new Order(data.order) || null;
        }

        this.form = this.createForm()
    },

    data() {
        return {
            order: null,
            clients: Client.all(),

            form: this.createForm(),
            showItemForm: false,

            headers: [
                { text: this.$t('general.name'), value: 'name' },
                { text: this.$t('general.quantity'), value: 'count' },
                { text: this.$t('general.unit_price'), value: 'unit_price' },
                { text: this.$t('general.discount'), value: 'discount_percent' },
                { text: '', value: 'actions', sortable: false }
            ],

            selectedItem: null,
            picker1: false,
            picker2: false,
        };
    },

    validations: {
        form: {
            last_activity_at: { required },
            client_id: { required },
            status: { required },
            iic: { requiredIf: requiredIf(form => locale === 'me') },
            fiscal_code: { requiredIf: requiredIf(form => locale === 'me') },
        }
    },

    methods: {
        createForm() {
            const currentDate = moment().format('YYYY-MM-DD');
            const zeroValue = '0.0000';

            return new Form(this, 'form', {
                id: this.order?.id || null,
                last_activity_at: this.order?.last_activity_at ? moment(this.order.last_activity_at).format('YYYY-MM-DD') : currentDate,
                due_date: this.order?.due_date ? moment(this.order.due_date).format('YYYY-MM-DD') : null,
                client_id: this.order?.client_id || null,
                status: this.order?.status || Order.STATUS_PAID,
                export: this.order?.export || false,
                iic: this.order?.iic || null,
                fiscal_code: this.order?.fiscal_code || null,
                paid_cash: zeroValue,
                paid_credit_card: zeroValue,
                paid_credit_card_business: zeroValue,
                paid_invoice: zeroValue,
                paid_advance: zeroValue,
                paid_other_noncash: zeroValue,
                paid_check: zeroValue,
                paid_voucher: zeroValue,
                paid_mobile_money: zeroValue,
                incoming: true,
                flag: 'manual'
            });
        },

        async openItemForm(item = null) {
            if(!this.form.validate()) return;

            this.selectedItem = item;
            this.showItemForm = true;
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('order_edit.order_item_permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                async result => {
                    if (!result) return;

                    await ApiService.delete(`/admin/orders/${this.order.id}/items/${item.id}`, {})

                    this.order.items = this.order.items.filter(i => i.hash !== item.hash)

                    this.$snackbar(this.$t('order_edit.order_item_deleted'), 'success');
                }
            );
        },

        async save() {
            if(!this.form.validate()) return;

            if (!this.form.id) {
                const response = await ApiService.post('/admin/orders', this.form);
                this.order = response.data.order;

                this.form.id = response.data.order.id;
                this.$snackbar(this.$t('order_edit.saved'), 'success');

                this.$router.replace({
                    name: 'adminOrderUpdate',
                    params: { id: response.data.order.id }
                });
            } else {
                const response = await ApiService.patch(`/admin/orders/${this.form.id}`, this.form);
                this.order = new Order(response.data.order);
                this.form.id = response.data.order.id;
                this.$snackbar(this.$t('order_edit.updated'), 'success');
            }
        },

        onItemSave(item) {
            const findIndex = this.order.items.findIndex(i => i.hash === item.hash)

            if(findIndex !== -1) {
                this.$set(this.order.items, findIndex, item);
            } else {
                this.order.items.unshift(item)
            }
        },

        goBack() {
            this.$router.push({ name: 'adminOrders' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.order-form {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .highlight-section {
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
        padding: 20px;
        font-size: 15px;

        .section-title {
            margin-bottom: 24px;
            font-size: 20px;
            color: black;

            .v-icon {
                color: black;
            }
        }
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }
}

.order-form-header {
    text-overflow: ellipsis;
    overflow-x: clip;
}
</style>
